import React from 'react';
import { Box, BoxProps } from '@chakra-ui/react';

export type LogoSize = 
  | 'favicon' 
  | 'navbar' 
  | 'navbar-large' 
  | 'navbar-xl'
  | 'hero' 
  | 'hero-large' 
  | 'hero-xl'
  | 'social' 
  | 'social-large'
  | 'print' 
  | 'print-large';

export interface SpeedcodeLogoProps extends Omit<BoxProps, 'size'> {
  size?: LogoSize;
  alt?: string;
  onClick?: () => void;
  href?: string;
}

const LOGO_SIZES = {
  favicon: { width: '16px', height: '9px' },
  navbar: { width: '40px', height: '24px' },
  'navbar-large': { width: '60px', height: '36px' },
  'navbar-xl': { width: '80px', height: '48px' },
  hero: { width: '120px', height: '73px' },
  'hero-large': { width: '180px', height: '109px' },
  'hero-xl': { width: '240px', height: '146px' },
  social: { width: '512px', height: '312px' },
  'social-large': { width: '1024px', height: '624px' },
  print: { width: '300px', height: '183px' },
  'print-large': { width: '600px', height: '366px' }
};

const LOGO_SOURCES = {
  favicon: '/images/logo/speedcode_logo_favicon_16x9.png',
  navbar: '/images/logo/speedcode_logo_navbar_40x24.png',
  'navbar-large': '/images/logo/speedcode_logo_navbar_60x36.png',
  'navbar-xl': '/images/logo/speedcode_logo_navbar_80x48.png',
  hero: '/images/logo/speedcode_logo_hero_120x73.png',
  'hero-large': '/images/logo/speedcode_logo_hero_180x109.png',
  'hero-xl': '/images/logo/speedcode_logo_hero_240x146.png',
  social: '/images/logo/speedcode_logo_social_512x312.png',
  'social-large': '/images/logo/speedcode_logo_social_1024x624.png',
  print: '/images/logo/speedcode_logo_print_300x182.png',
  'print-large': '/images/logo/speedcode_logo_print_600x365.png'
};

const LOGO_SOURCES_WEBP = {
  favicon: '/images/logo/speedcode_logo_favicon_16x9.webp',
  navbar: '/images/logo/speedcode_logo_navbar_40x24.webp',
  'navbar-large': '/images/logo/speedcode_logo_navbar_60x36.webp',
  'navbar-xl': '/images/logo/speedcode_logo_navbar_80x48.webp',
  hero: '/images/logo/speedcode_logo_hero_120x73.webp',
  'hero-large': '/images/logo/speedcode_logo_hero_180x109.webp',
  'hero-xl': '/images/logo/speedcode_logo_hero_240x146.webp',
  social: '/images/logo/speedcode_logo_social_512x312.webp',
  'social-large': '/images/logo/speedcode_logo_social_1024x624.webp',
  print: '/images/logo/speedcode_logo_print_300x182.webp',
  'print-large': '/images/logo/speedcode_logo_print_600x365.webp'
};

export const SpeedcodeLogo: React.FC<SpeedcodeLogoProps> = ({
  size = 'navbar',
  alt = 'Speedcode Logo',
  onClick,
  href,
  ...boxProps
}) => {
  const logoSize = LOGO_SIZES[size];
  const pngSource = LOGO_SOURCES[size];
  const webpSource = LOGO_SOURCES_WEBP[size];

  const logoElement = (
    <Box
      as="picture"
      display="inline-block"
      {...logoSize}
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      {...boxProps}
    >
      <source srcSet={webpSource} type="image/webp" />
      <Box
        as="img"
        src={pngSource}
        alt={alt}
        width="100%"
        height="100%"
        objectFit="contain"
        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
          // Fallback to PNG if WebP fails
          const target = e.target as HTMLImageElement;
          if (target.src !== pngSource) {
            target.src = pngSource;
          }
        }}
      />
    </Box>
  );

  if (href) {
    return (
      <Box as="a" href={href} display="inline-block">
        {logoElement}
      </Box>
    );
  }

  return logoElement;
};

// Convenience components for common use cases
export const NavbarLogo: React.FC<Omit<SpeedcodeLogoProps, 'size'>> = (props) => (
  <SpeedcodeLogo size="navbar" {...props} />
);

export const HeroLogo: React.FC<Omit<SpeedcodeLogoProps, 'size'>> = (props) => (
  <SpeedcodeLogo size="hero-large" {...props} />
);

export const FooterLogo: React.FC<Omit<SpeedcodeLogoProps, 'size'>> = (props) => (
  <SpeedcodeLogo size="navbar" {...props} />
);

export default SpeedcodeLogo; 